import { useCallback, useEffect, useState } from 'react';
import { setGlobalFilter } from '../store/slice/global-filters';
import { useAppDispatch } from './useAppDispatch';
import { GeoClass } from '../interface/response/GeoFiltersResponse';

/**
 * `useOtherFilter` is a custom React hook for managing filters like Games and PaymentSource,
 *
 * - `handleChange`
 * Handles selection changes for multi-select filter components.
 *
 * This function toggles the selection of an option within a specific column filter.
 * If the option is already selected, it will be removed. If it’s not selected,
 * it will be added to the current selection array.
 *
 * @param {string} col - The name of the column (filter key) being modified.
 * @param {any} option - The option object being selected or deselected.
 *                       Expected to contain at least an `ID` property.
 *
 * @example
 * handleChange('Games', { ID: 1, Name: 'MyLucky6' });
 *
 * @returns {void}
 *
 * - `selectAll`
 * Handles the "Select All" / "Deselect All" action for a specific filter column.
 *
 * If all available options are already selected, this will clear the selection.
 * Otherwise, it will select all available options for the given column.
 *
 * @param {string} col - The name of the column (filter key) being modified.
 * @param {any[]} options - All possible options for the given column.
 *
 * @returns {void}
 *
 * Effect that updates the count of selected options per filter column.
 *
 * - Iterates over all filter columns and counts the number of selected items in each.
 * - Compares the newly computed counts with the existing `numberOfSelected` state.
 * - Updates the state only if there is a change, to avoid unnecessary re-renders.
 *
 */

export const useOtherFilter = (filters: any, setFilters: (f: GeoClass) => void, columns: string[]) => {
  const dispatch = useAppDispatch();
  const [numberOfSelected, setNumberOfSelected] = useState<any>({});

  const selectAll = useCallback(
    (col: any, options: GeoClass[]) => {
      const selected = filters[col] || [];
      const isAllSelected = selected.length === options.length;

      const newValue = isAllSelected ? [] : options;

      dispatch(setGlobalFilter({ key: col, value: newValue }));
    },
    [filters, dispatch],
  );

  useEffect(() => {
    const newCounts: Record<string, number> = {};

    columns.forEach((col) => {
      const selected = filters[col] || [];
      newCounts[col] = selected.length;
    });

    const isSame = JSON.stringify(numberOfSelected) === JSON.stringify(newCounts);
    if (!isSame) {
      setNumberOfSelected(newCounts);
    }
  }, [filters, columns, numberOfSelected]);

  const handleChange = useCallback(
    (col: any, option: GeoClass) => {
      const currentSelections = filters[col] || [];
      const isSelected = currentSelections.some((item: GeoClass) => item.ID === option.ID);

      const newSelections = isSelected
        ? currentSelections.filter((item: GeoClass) => item.ID !== option.ID)
        : [...currentSelections, option];

      dispatch(setGlobalFilter({ key: col, value: newSelections }));
    },
    [filters, dispatch],
  );

  return {
    numberOfSelected,
    selectAll,
    handleChange,
  };
};

export enum Games {
  GAMES = 'Games',
}
